import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from './Navbar';
import MysticalParticles from './MysticalParticles';
import ProfilePicture from './ProfilePicture';
import { User, Coins, Users, Image, Calendar, Settings, Upload, Camera, X } from 'lucide-react';

const MystveilProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('mystveil_token'));
  const [showProfilePictureUpload, setShowProfilePictureUpload] = useState(false);
  const [profilePictureFile, setProfilePictureFile] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const loadProfile = async () => {
      if (!token) {
        setError('Please log in to view your profile');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await fetch('https://api.4fingerstudios.com/api/mystveil/profile', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          if (response.status === 401) {
            setError('Session expired. Please log in again.');
            localStorage.removeItem('mystveil_token');
            setToken(null);
          } else {
            throw new Error(`Failed to load profile: ${response.status}`);
          }
          return;
        }

        const data = await response.json();
        setProfile(data);
      } catch (err) {
        setError('Failed to load profile');
        console.error('Profile load error:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [token]);

  const handleLogout = () => {
    localStorage.removeItem('mystveil_token');
    setToken(null);
    setProfile(null);
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfilePictureFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleProfilePictureUpload = async () => {
    if (!profilePictureFile) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('profilePicture', profilePictureFile);

      const response = await fetch('https://api.4fingerstudios.com/api/mystveil/profile/picture', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Failed to upload profile picture');
      }

      const data = await response.json();
      setProfile({...profile, profilePicture: data.profilePicture});
      setShowProfilePictureUpload(false);
      setProfilePictureFile(null);
      setProfilePicturePreview(null);
    } catch (err) {
      console.error('Profile picture upload error:', err);
      setError('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  const resetProfilePictureForm = () => {
    setProfilePictureFile(null);
    setProfilePicturePreview(null);
    setShowProfilePictureUpload(false);
  };

  if (!token) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Mystveil Profile</h1>
            <p className="mystical-text mb-6">Please log in to view your profile</p>
            <Link to="/auth/login" className="mystical-button">
              Login
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card">
            <p className="mystical-text">Loading your profile...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col relative">
        <div className="fixed inset-0 -z-10">
          <img 
            src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
            alt="Mystveil Township Map" 
            className="w-full h-full object-cover opacity-30 blur-sm" 
          />
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
        </div>
        <MysticalParticles />
        <Navbar />
        
        <main className="flex-grow flex items-center justify-center px-4 py-8">
          <div className="mystical-card max-w-md mx-auto text-center">
            <h1 className="mystical-title mb-6">Error</h1>
            <p className="mystical-text mb-6 text-red-600">{error}</p>
            <button onClick={handleLogout} className="mystical-button">
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Magical Background */}
      <div className="fixed inset-0 -z-10">
        <img 
          src="https://cdn.4fingerstudios.com/Mystveil/Mystveil_map.png" 
          alt="Mystveil Township Map" 
          className="w-full h-full object-cover opacity-30 blur-sm" 
        />
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-pink-900/20 to-purple-900/20"></div>
      </div>

      {/* Magical Particles */}
      <MysticalParticles />

      {/* Navigation */}
      <Navbar />

      {/* Main Content */}
      <main className="flex-grow px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="mystical-card mb-8">
            <div className="flex items-center justify-between mb-6">
              <h1 className="mystical-title text-black">
                Mystveil Profile
              </h1>
              <div className="flex space-x-3">
                <button 
                  onClick={() => setShowProfilePictureUpload(true)} 
                  className="mystical-button bg-purple-600 hover:bg-purple-700"
                >
                  <Camera className="mr-2" size={20} />
                  Update Profile Picture
                </button>
                <button onClick={handleLogout} className="mystical-button">
                  Logout
                </button>
              </div>
            </div>

            {/* Profile Picture Section */}
            <div className="mystical-card bg-white bg-opacity-95 mb-8">
              <div className="flex items-center space-x-6">
                <div className="relative">
                  <ProfilePicture 
                    src={profile?.profilePicture} 
                    alt="Profile" 
                    size="xl"
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => setShowProfilePictureUpload(true)}
                  />
                  <div className="absolute -bottom-2 -right-2 bg-purple-600 rounded-full p-2 cursor-pointer hover:bg-purple-700 transition-colors">
                    <Camera className="text-white" size={16} />
                  </div>
                </div>
                <div>
                  <h2 className="mystical-subtitle text-black text-2xl">{profile?.username}</h2>
                  <p className="mystical-text text-gray-600">{profile?.role}</p>
                  <p className="mystical-text text-gray-500 text-sm">
                    Member since {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'Unknown'}
                  </p>
                </div>
              </div>
            </div>

            {/* Profile Picture Upload Modal */}
            {showProfilePictureUpload && (
              <div className="mystical-card bg-white bg-opacity-95 mb-8">
                <h3 className="mystical-subtitle text-black text-xl mb-4">
                  Update Profile Picture
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block mystical-text text-black font-bold mb-2">
                      Choose Profile Picture
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleProfilePictureChange}
                      className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-700 focus:border-transparent"
                    />
                  </div>
                  
                  {profilePicturePreview && (
                    <div className="flex items-center space-x-4">
                      <img 
                        src={profilePicturePreview} 
                        alt="Preview" 
                        className="w-20 h-20 rounded-full object-cover border-2 border-purple-300"
                      />
                      <div>
                        <p className="mystical-text text-gray-700">Preview of your new profile picture</p>
                        <p className="mystical-text text-gray-500 text-sm">This will be displayed across the site</p>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex space-x-3">
                    <button 
                      onClick={handleProfilePictureUpload}
                      disabled={!profilePictureFile || uploading}
                      className="mystical-button bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                    >
                      <Upload className="mr-2" size={20} />
                      {uploading ? 'Uploading...' : 'Upload Picture'}
                    </button>
                    <button 
                      onClick={resetProfilePictureForm}
                      className="mystical-button bg-gray-600 hover:bg-gray-700"
                    >
                      <X className="mr-2" size={20} />
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {/* Profile Stats */}
              <div className="mystical-card bg-white bg-opacity-95">
                <div className="flex items-center space-x-3 mb-4">
                  <User className="text-purple-800" size={24} />
                  <h3 className="mystical-subtitle text-black">Profile</h3>
                </div>
                <p className="mystical-text text-gray-700">
                  <strong>Username:</strong> {profile?.username}
                </p>
                <p className="mystical-text text-gray-700">
                  <strong>Role:</strong> {profile?.role}
                </p>
                <p className="mystical-text text-gray-700">
                  <strong>Member Since:</strong> {profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'Unknown'}
                </p>
              </div>

              {/* Chip Tracker */}
              <div className="mystical-card bg-white bg-opacity-95">
                <div className="flex items-center space-x-3 mb-4">
                  <Coins className="text-yellow-600" size={24} />
                  <h3 className="mystical-subtitle text-black">Chip Tracker</h3>
                </div>
                <p className="mystical-text text-gray-700">
                  <strong>Available Chips:</strong> {profile?.chipTracker?.availableChips || 0}
                </p>
                <p className="mystical-text text-gray-700">
                  <strong>Total Earned:</strong> {profile?.chipTracker?.totalEarned || 0}
                </p>
                <p className="mystical-text text-gray-700">
                  <strong>Total Spent:</strong> {profile?.chipTracker?.totalSpent || 0}
                </p>
              </div>

              {/* Veilkin Characters */}
              <div className="mystical-card bg-white bg-opacity-95">
                <div className="flex items-center space-x-3 mb-4">
                  <Users className="text-blue-600" size={24} />
                  <h3 className="mystical-subtitle text-black">Characters</h3>
                </div>
                <p className="mystical-text text-gray-700">
                  <strong>Total Characters:</strong> {profile?.veilkinProfiles?.length || 0}
                </p>
                <p className="mystical-text text-gray-700">
                  <strong>Max Slots:</strong> 2 (Beta)
                </p>
                <Link to="/characters" className="mystical-button mt-2">
                  Manage Characters
                </Link>
              </div>

              {/* Image Submissions */}
              <div className="mystical-card bg-white bg-opacity-95">
                <div className="flex items-center space-x-3 mb-4">
                  <Image className="text-green-600" size={24} />
                  <h3 className="mystical-subtitle text-black">Submissions</h3>
                </div>
                <p className="mystical-text text-gray-700">
                  <strong>Total Submissions:</strong> {profile?.imageSubmissions?.length || 0}
                </p>
                <p className="mystical-text text-gray-700">
                  <strong>Approved:</strong> {profile?.imageSubmissions?.filter(s => s.status === 'approved').length || 0}
                </p>
                <Link to="/submit" className="mystical-button mt-2">
                  Submit Artwork
                </Link>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Characters */}
            <div className="mystical-card">
              <h3 className="mystical-subtitle text-black text-xl mb-6">
                Recent Characters
              </h3>
              {profile?.veilkinProfiles?.length > 0 ? (
                <div className="space-y-4">
                  {profile.veilkinProfiles.slice(0, 3).map(character => (
                    <div key={character.id} className="mystical-card bg-white bg-opacity-95">
                      <div className="flex items-start space-x-3">
                        {character.characterImage && (
                          <img 
                            src={character.characterImage} 
                            alt={character.characterName}
                            className="w-16 h-16 rounded-md object-cover"
                          />
                        )}
                        <div className="flex-1">
                          <h4 className="mystical-subtitle text-black text-lg">
                            {character.characterName}
                          </h4>
                          <p className="mystical-text text-gray-600 text-sm">
                            {character.characterDescription || 'No description provided'}
                          </p>
                          <Link 
                            to={`/characters/${character.id}`} 
                            className="mystical-button mt-2"
                          >
                            View Details
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mystical-card bg-white bg-opacity-95">
                  <p className="mystical-text text-gray-600 text-center">
                    No characters created yet
                  </p>
                  <Link to="/characters" className="mystical-button mt-4">
                    Create Your First Character
                  </Link>
                </div>
              )}
            </div>

            {/* Recent Submissions */}
            <div className="mystical-card">
              <h3 className="mystical-subtitle text-black text-xl mb-6">
                Recent Submissions
              </h3>
              {profile?.imageSubmissions?.length > 0 ? (
                <div className="space-y-4">
                  {profile.imageSubmissions.slice(0, 3).map(submission => (
                    <div key={submission.id} className="mystical-card bg-white bg-opacity-95">
                      <div className="flex items-start space-x-3">
                        <img 
                          src={submission.imageUrl} 
                          alt={submission.imageTitle}
                          className="w-16 h-16 rounded-md object-cover"
                        />
                        <div className="flex-1">
                          <h4 className="mystical-subtitle text-black text-lg">
                            {submission.imageTitle}
                          </h4>
                          <p className="mystical-text text-gray-600 text-sm">
                            Status: <span className={`font-bold ${
                              submission.status === 'approved' ? 'text-green-600' :
                              submission.status === 'pending' ? 'text-yellow-600' :
                              'text-red-600'
                            }`}>
                              {submission.status}
                            </span>
                          </p>
                          <p className="mystical-text text-gray-600 text-sm">
                            Chips: {submission.totalChips}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mystical-card bg-white bg-opacity-95">
                  <p className="mystical-text text-gray-600 text-center">
                    No submissions yet
                  </p>
                  <Link to="/submit" className="mystical-button mt-4">
                    Submit Your First Artwork
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mystical-card mt-8">
            <h3 className="mystical-subtitle text-black text-xl mb-6">
              Quick Actions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/characters" className="mystical-button">
                <Users className="mr-2" size={20} />
                Manage Characters
              </Link>
              <Link to="/upgrades" className="mystical-button">
                <Settings className="mr-2" size={20} />
                View Upgrades
              </Link>
              <Link to="/submit" className="mystical-button">
                <Image className="mr-2" size={20} />
                Submit Artwork
              </Link>
              <Link to="/gallery" className="mystical-button">
                <Calendar className="mr-2" size={20} />
                View Gallery
              </Link>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <Link to="/" className="mystical-button">
              Return Home
            </Link>
            <Link to="/events" className="mystical-button">
              View Events
            </Link>
            <Link to="/guide" className="mystical-button">
              View Guides
            </Link>
          </div>
        </div>
      </main>

      {/* Mystical Footer */}
      <footer className="mystical-nav py-6 mt-auto">
        <div className="container mx-auto px-4 text-center">
          <p className="mystical-text font-bold">
            © 2025 4fingerstudios <br /> 
            © 2024 Reckless Dreams. All Rights Reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MystveilProfile;
